import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";

import { isNative } from "@/lib/platform";
import { commands } from "@/lib/platform/generated/bindings.gen";
import { type PermissionKind, type PermissionStatus, isPermissionStatus } from "./types";

const POLL_INTERVAL_MS = 1_000;

function queryKey(kind: PermissionKind) {
  return ["permission", kind] as const;
}

async function checkPermission(kind: PermissionKind): Promise<PermissionStatus> {
  // commands.checkPermission returns PermissionStatus directly (no Result wrapper)
  const raw = await commands.checkPermission(kind);
  if (!isPermissionStatus(raw)) {
    throw new Error(`check_permission(${kind}) returned unexpected: ${JSON.stringify(raw)}`);
  }
  return raw;
}

/**
 * Live permission status for one kind. Polls every 1s while the tab/window is
 * visible (react-query pauses background refetch by default). `request`/`reset`
 * are mutations that re-sync status on completion.
 */
export function usePermission(kind: PermissionKind) {
  const qc = useQueryClient();

  const query = useQuery({
    queryKey: queryKey(kind),
    queryFn: () => checkPermission(kind),
    refetchInterval: POLL_INTERVAL_MS,
    enabled: isNative,
  });

  const requestMutation = useMutation({
    mutationFn: async (): Promise<PermissionStatus> => {
      const res = await commands.requestPermission(kind);
      if (res.status === "error") throw new Error(res.error);
      return isPermissionStatus(res.data) ? res.data : checkPermission(kind);
    },
    onSuccess: (status) => {
      // setQueryData reflects the new status immediately (optimistic update),
      // then invalidateQueries forces a pessimistic re-verify of the OS state
      // on the next tick — don't trust the request() return value alone.
      qc.setQueryData(queryKey(kind), status);
      void qc.invalidateQueries({ queryKey: queryKey(kind) });
    },
  });

  const resetMutation = useMutation({
    mutationFn: async (): Promise<void> => {
      const res = await commands.resetPermissionKind(kind);
      if (res.status === "error") throw new Error(res.error);
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: queryKey(kind) }),
  });

  return {
    status: query.data,
    isPending: requestMutation.isPending || resetMutation.isPending,
    error: query.error,
    refetch: query.refetch,
    request: () => requestMutation.mutateAsync(),
    reset: () => resetMutation.mutateAsync(),
    open: async (): Promise<void> => {
      const res = await commands.openPermission(kind);
      if (res.status === "error") throw new Error(res.error);
    },
  };
}
